import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import * as naturalCompare from 'natural-compare-lite';
import { LoadStatus } from '../../constants/constants';
import api from '../../services/DataLayer';
import { sorter } from '../../utils/sortHelpers';
import { signOut } from './authenticationReducer';
import { selectFindingsList } from './findingsReducer';

export const AssessmentSteps = Object.freeze({
  ASSESSMENT_SELECTION: 'assessmentSelection',
  PLAN: 'plan',
  EVALUATION: 'evaluation',
  REVIEW: 'review',
  DONE: 'done',
});

export const PdfDownloadStatus = Object.freeze({
  INITIAL: 'initial',
  IN_PROGRESS: 'inProgess',
  DONE: 'done',
});

const initialState = {
  assessments: [],
  assessmentStep: AssessmentSteps.ASSESSMENT_SELECTION,
  contactFilters: [],
  conclusionFilters: [],
  filterContacts: false,
  filterStatus: false,
  filterConclusion: false,
  loadStatus: LoadStatus.INITIAL,
  organizationId: '',
  pdfDownloadStatus: PdfDownloadStatus.INITIAL,
  personList: [],
  saveOnId: null,
  scrollReferenceId: null,
  scrollPosition: 0,
  controlItemInFocus: null,
  selectedAssessment: {},
  selectedContacts: [],
  selectedGroupId: '',
  statusFilters: [],
  unsavedChanges: false,
};

export const EvaluationStatus = Object.freeze({
  NOT_STARTED: 'notStarted',
  IN_PROGRESS: 'inProgress',
  PENDING: 'pending',
  COMPLETED: 'completed',
  list() {
    return [this.NOT_STARTED, this.IN_PROGRESS, this.PENDING, this.COMPLETED];
  },
});

export const AssessmentType = Object.freeze({
  AUDIT: 'audit',
  GAP: 'gap',
  list() {
    return [this.AUDIT, this.GAP];
  },
});

export const UsualJustifications = Object.freeze({
  NON_RELEVANT_INDUSTRY: 'nonRelevantIndustry',
  USUAL_2: 'usual2',
  USUAL_3: 'usual3',
  USUAL_4: 'usual4',
  USUAL_5: 'usual5',
  USUAL_6: 'usual6',
  list() {
    return [this.NON_RELEVANT_INDUSTRY, this.USUAL_2, this.USUAL_3, this.USUAL_4, this.USUAL_5, this.USUAL_6];
  },
});

export const ApplicabilityReasons = Object.freeze({
  LR: 'lr',
  CO: 'co',
  BR: 'br',
  RRA: 'rra',
  list() {
    return [this.LR, this.CO, this.BR, this.RRA];
  },
});

export const ItemTypes = Object.freeze({
  CONTROL: 'control',
  GROUP: 'group',
  list() {
    return [this.CONTROL, this.GROUP];
  },
});

export const Conclusions = Object.freeze({
  OK: 'ok',
  ISSUES: 'issues',
  list() {
    return [this.OK, this.ISSUES];
  },
});

const itemDefaults = {
  id: '',
  inScope: true,
  contacts: [],
  evaluationStatus: EvaluationStatus.NOT_STARTED,
  evidenceLinks: [],
  type: ItemTypes.CONTROL,
  notes: '',
  conclusion: '',
};

const assessmentDefaults = {
  name: '',
  frameworkId: '',
  assessmentType: AssessmentType.GAP,
  description: '',
  completion: '0%',
  content: {},
};

export const FilterTypes = Object.freeze({
  STATUS: 'status',
  CONTACT: 'contact',
  CONCLUSION: 'conclusion',
});

const isDirectSub = (item, screen) => {
  if (item.startsWith(screen)) {
    const ending = item.replace(screen, '').slice(1);
    // Direct sub must have more characters than just the screen and
    // not contain any dots (since then it's not direct sub)
    return ending.length > 0 && !ending.includes('.');
  }
  return false;
};

const getContacts = (content) => {
  const entries = Object.values(content).filter((entry) => entry.type !== undefined);
  const contactList = [...new Set(entries.flatMap((entry) => entry.contacts))];
  return contactList;
};

export const initialize = createAsyncThunk('assessments/initialize', async (organizationId, { getState }) => {
  if (getState().assessments.organizationId !== organizationId) {
    let assessments = [];
    try {
      assessments = await api.getOrganizationAssessments(organizationId);
    } catch (e) {
      // TODO: ensure this is a 404 not found, ie no assessments were found for given orgId
    }
    return { assessments, id: organizationId };
  }
  return undefined;
});

export const selectAssessment = createAsyncThunk(
  'assessments/selectAssessment',
  async (assessmentDetails, { getState, rejectWithValue }) => {
    const { selectedAssessment } = getState().assessments;
    if (selectedAssessment.id === undefined || selectedAssessment.id !== assessmentDetails.assessmentId) {
      const organizationId = getState().organizations.selectedOrganization.id;
      const { userList, contactList } = getState().organizations;
      try {
        const assessment = await api.getOrganizationAssessment(
          organizationId,
          assessmentDetails.assessmentId,
          assessmentDetails.assessmentType,
        );
        return { assessment, userList, contactList };
      } catch (error) {
        return rejectWithValue(error);
      }
    }
    return undefined;
  },
);

export const createAssessment = createAsyncThunk(
  'assessments/createAssessment',
  async (assessmentFormData, { getState }) => {
    const framework =
      getState().frameworks.standardFrameworks.find((fw) => fw.id === assessmentFormData.framework) ??
      getState().frameworks.customFrameworks.find((fw) => fw.id === assessmentFormData.framework);
    const assessmentData = {
      name: assessmentFormData.name,
      frameworkId: assessmentFormData.framework,
      assessmentType: assessmentFormData.assessmentType,
      description: assessmentFormData.description,
      content: {
        details: {
          scope: '',
          criteria: '',
          objectives: '',
          auditees: [],
        },
      },
      completion: '0%',
      id: '',
      done: false,
    };
    if (framework.controlObjectives) {
      framework.controlObjectives.forEach((item) => {
        assessmentData.content[item] = { ...itemDefaults, id: item };
      });
      framework.controlGroups.forEach((item) => {
        assessmentData.content[item] = { ...itemDefaults, id: item, type: ItemTypes.GROUP };
      });
    } else {
      Object.values(framework.controls).forEach((item) => {
        assessmentData.content[item.id] = { ...itemDefaults, id: item.id, type: item.type };
      });
    }
    const assessmentId = await api.createOrganizationAssessment(
      getState().organizations.selectedOrganization.id,
      assessmentData,
    );
    assessmentData.id = assessmentId;
    return assessmentData;
  },
);

export const createCopy = createAsyncThunk('assessments/createCopy', async (assessmentFormData, { getState }) => {
  const baseAssessment = getState().assessments.selectedAssessment;
  const { frameworkId, assessmentType } = baseAssessment;
  const content = { ...baseAssessment.content };
  Object.keys(content).forEach((key) => {
    if (content[key].type === ItemTypes.CONTROL) {
      content[key] = { ...content[key], evaluationStatus: EvaluationStatus.NOT_STARTED };
    }
  });
  const assessmentData = {
    name: assessmentFormData.name,
    description: assessmentFormData.description,
    frameworkId,
    content: { ...content, baseId: baseAssessment.id },
    assessmentType,
    completion: '0%',
    id: '',
    done: false,
  };
  const assessmentId = await api.createOrganizationAssessment(
    getState().organizations.selectedOrganization.id,
    assessmentData,
  );
  assessmentData.id = assessmentId;
  return assessmentData;
});

export const saveUnsavedChanges = createAsyncThunk('assessments/saveUnsavedChanges', async (id, { getState }) => {
  const assessmentState = getState().assessments;
  // When unsaved changes are pending and the caller is either deliberately empty or the one that is set in state, update the assessment
  if (assessmentState.unsavedChanges && (id === '' || id === assessmentState.saveOnId)) {
    const _ = await api.updateOrganizationAssessment(
      getState().organizations.selectedOrganization.id,
      getState().assessments.selectedAssessment,
    );
    return true;
  }
  return undefined;
});

export const saveAssessment = createAsyncThunk('assessments/saveAssessment', async (assessmentData, { getState }) => {
  const _ = await api.updateOrganizationAssessment(getState().organizations.selectedOrganization.id, assessmentData);
  return assessmentData;
});

export const saveOnLogout = createAsyncThunk(
  'assessments/saveOnLogout',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const _ = await api.updateOrganizationAssessment(
        getState().organizations.selectedOrganization.id,
        getState().assessments.selectedAssessment,
      );
      dispatch(signOut());
    } catch (error) {
      rejectWithValue(error);
    }
  },
);

export const markAssessmentDone = createAsyncThunk(
  'assessments/markAssessmentDone',
  async (_, { getState, rejectWithValue }) => {
    try {
      const assessmentData = { ...getState().assessments.selectedAssessment };
      assessmentData.done = true;
      const id = await api.updateOrganizationAssessment(
        getState().organizations.selectedOrganization.id,
        assessmentData,
      );
      return id;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error);
    }
  },
);

export const assessmentSlice = createSlice({
  name: 'assessments',
  initialState,
  reducers: {
    setFilter: (state, action) => {
      switch (action.payload.type) {
        case FilterTypes.CONTACT:
          state.contactFilters = action.payload.value;
          state.filterContacts = true;
          break;
        case FilterTypes.STATUS:
          state.statusFilters = action.payload.value;
          state.filterStatus = true;
          break;
        case FilterTypes.CONCLUSION:
          state.conclusionFilters = action.payload.value;
          state.filterConclusion = true;
          break;
        default:
          // no-op
          break;
      }
    },
    clearFilter: (state, action) => {
      switch (action.payload) {
        case FilterTypes.CONTACT:
          state.contactFilters = [...state.selectedContacts, 'unassigned'];
          state.filterContacts = false;
          break;
        case FilterTypes.STATUS:
          state.statusFilters = EvaluationStatus.list();
          state.filterStatus = false;
          break;
        case FilterTypes.CONCLUSION:
          state.conclusionFilters = [...Conclusions.list(), 'undefined'];
          state.filterConclusion = false;
          break;
        // If no type given, reset all filters
        default:
          state.conclusionFilters = [...Conclusions.list(), 'undefined'];
          state.contactFilters = [...state.selectedContacts, 'unassigned'];
          state.statusFilters = EvaluationStatus.list();
          state.filterConclusion = false;
          state.filterStatus = false;
          state.filterContacts = false;
          break;
      }
    },
    clearSelectedAssessment: (state, _) => {
      state.loadStatus = LoadStatus.INITIAL;
      state.selectedAssessment = {};
    },
    clearScrollReference: (state, _) => {
      state.scrollReferenceId = null;
    },
    setScrollPosition: (state, action) => {
      state.scrollPosition = action.payload;
    },
    setControlItemInFocus: (state, action) => {
      state.controlItemInFocus = action.payload;
    },
    goToControl: (state, action) => {
      // check if payload is control -> select it & group is 1 level up
      // payload is group -> find first actual control
      let controlId;
      let groupId;
      if (state.selectedAssessment.content[action.payload].type === ItemTypes.GROUP) {
        const separator = action.payload.split(':').length > 2 ? '.' : '';
        const control = Object.values(state.selectedAssessment.content)
          .filter(
            (item) =>
              item.inScope && item.type === ItemTypes.CONTROL && item.id.startsWith(`${action.payload}${separator}`),
          )
          .sort((a, b) => naturalCompare(a.id, b.id));
        controlId = control[0].id;
        groupId = action.payload;
      } else {
        groupId = action.payload.split('.').slice(0, -1).join('.');
        controlId = action.payload;
      }
      state.selectedGroupId = groupId;
      state.scrollReferenceId = controlId;
      state.assessmentStep = AssessmentSteps.EVALUATION;
    },
    setPdfDownloadStatus: (state, action) => {
      switch (action.payload) {
        case PdfDownloadStatus.INITIAL:
          state.pdfDownloadStatus = action.payload;
          break;
        case PdfDownloadStatus.IN_PROGRESS:
          state.pdfDownloadStatus = action.payload;
          state.pdfData = null;
          break;
        case PdfDownloadStatus.DONE:
          if (state.pdfDownloadStatus === PdfDownloadStatus.IN_PROGRESS) {
            state.pdfDownloadStatus = action.payload;
          }
          break;
        default:
          break;
      }
    },
    selectGroup: (state, action) => {
      state.selectedGroupId = action.payload;
    },
    setAssessmentLoading: (state, _) => {
      state.loadStatus = LoadStatus.IN_PROGRESS;
    },
    setAssessmentStep: (state, action) => {
      state.assessmentStep = action.payload;
    },
    updateCompletion: (state, action) => {
      if (
        action.payload !== undefined &&
        state.selectedAssessment.id &&
        state.selectedAssessment.completion !== action.payload
      ) {
        const index = state.assessments.findIndex((ass) => ass.id === state.selectedAssessment.id);
        const newState = `${action.payload}%`;
        const newList = [...state.assessments];
        newList[index] = { ...newList[index], completion: newState };
        state.assessments = newList;
        state.selectedAssessment.completion = newState;
      }
    },
    updateContacts: (state, action) => {
      const newState = { ...state.selectedAssessment };
      action.payload.forEach((item) => {
        if (state.saveOnId === null) {
          state.saveOnId = item.id; // Denotes the first edit caller to minimize update API calls
        }
        newState.content[item.id] = {
          ...newState.content[item.id],
          ...item,
        };
      });
      state.selectedContacts = getContacts(newState.content);
      state.unsavedChanges = true;
      state.selectedAssessment = newState;
    },
    updateDetails: (state, action) => {
      const newState = { ...state.selectedAssessment };
      newState.content.details = action.payload;
      state.unsavedChanges = true;
      state.selectedAssessment = newState;
    },
    updateGroups: (state, action) => {
      const newState = { ...state.selectedAssessment };
      action.payload.forEach((item) => {
        if (state.saveOnId === null) {
          state.saveOnId = item.id; // Denotes the first edit caller to minimize update API calls
        }
        newState.content[item.id] = {
          ...newState.content[item.id],
          ...item,
        };
      });
      state.unsavedChanges = true;
      state.selectedAssessment = newState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initialize.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          state.organizationId = action.payload.id;
          state.assessments = action.payload.assessments;
        }
      })
      .addCase(createCopy.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          state.assessments = [action.payload, ...state.assessments];
        }
      })
      .addCase(createAssessment.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          state.assessments = [action.payload, ...state.assessments];
        }
      })
      .addCase(markAssessmentDone.fulfilled, (state, action) => {
        const newState = { ...state, selectedAssessment: { ...state.selectedAssessment, done: true } };
        const newAssessments = [...state.assessments];
        const index = state.assessments.findIndex((ass) => ass.id === action.payload);
        newAssessments[index] = { ...newAssessments[index], done: true };
        newState.assessments = newAssessments;
        return newState;
      })
      .addCase(markAssessmentDone.rejected, (_, __) => {
        // TODO: Error state handling
      })
      .addCase(saveUnsavedChanges.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          state.unsavedChanges = false;
          state.saveOnId = null; // Reset call id
        }
      })
      .addCase(saveAssessment.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          const index = state.assessments.findIndex((ass) => ass.id === action.payload.id);
          const newState = [...state.assessments];
          if (index >= 0) {
            newState[index] = action.payload;
          } else {
            newState.unshift(action.payload);
          }
          state.assessments = newState;
        }
      })
      .addCase(selectAssessment.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          const newState = { ...state };
          newState.selectedAssessment = action.payload.assessment;
          const groups = Object.values(newState.selectedAssessment.content)
            .filter((item) => item.inScope && item.type === ItemTypes.GROUP)
            .sort((a, b) => naturalCompare(a.id, b.id));
          newState.selectedGroupId = groups[0].id;
          newState.statusFilters = EvaluationStatus.list();
          newState.conclusionFilters = [...Conclusions.list(), 'undefined'];
          const entries = Object.values(action.payload.assessment.content).filter((entry) => entry.type !== undefined);
          const contactList = [...new Set(entries.flatMap((entry) => entry.contacts))];
          newState.selectedContacts = contactList;
          newState.contactFilters = [...contactList, 'unassigned'];
          newState.loadStatus = LoadStatus.SUCCESS;
          return newState;
        }
        return { ...state, loadStatus: LoadStatus.SUCCESS };
      })
      // TODO: error handling
      .addCase(selectAssessment.rejected, (state, _) => ({ ...state, loadStatus: LoadStatus.FAILURE }))
      .addCase('resetState', (_, __) => ({ ...initialState }));
  },
});

const { actions, reducer } = assessmentSlice;

export default reducer;

export const {
  setFilter: checkFilter,
  clearFilter,
  clearSelectedAssessment,
  clearScrollReference,
  setScrollPosition,
  setControlItemInFocus,
  goToControl,
  setPdfDownloadStatus,
  selectGroup,
  setAssessmentLoading,
  setAssessmentStep,
  setFilter,
  updateCompletion,
  updateContacts,
  updateDetails,
  updateGroups,
} = actions;

const selectAssessments = (state) => state.assessments.assessments;

export const selectCurrentStep = (state) => state.assessments.assessmentStep;

export const selectScrollReferenceId = (state) => state.assessments.scrollReferenceId;

export const selectScrollPosition = (state) => state.assessments.scrollPosition;

export const selectControlItemInFocus = (state) => state.assessments.controlItemInFocus;

export const selectInProgressAssessmentList = createSelector(selectAssessments, (assessments) =>
  assessments.filter((ass) => !ass.done),
);

export const selectDoneAssessmentList = createSelector(selectAssessments, (assessments) =>
  assessments.filter((ass) => ass.done),
);

export const selectDoneAssessmentIdList = createSelector(selectDoneAssessmentList, (assessmentList) =>
  assessmentList.map((ass) => ass.id),
);

export const selectSelectedAssessment = (state) => state.assessments.selectedAssessment;

export const selectSelectedContacts = (state) => state.assessments.selectedContacts;

export const selectAssessmentLoadStatus = (state) => state.assessments.loadStatus;

export const selectAssessmentUnsavedChanges = (state) => state.assessments.unsavedChanges;

export const selectAssessmentName = createSelector(selectSelectedAssessment, (assessment) => assessment.name, {
  memoizeOptions: {
    equalityCheck: (a, b) => a.id === b.id,
  },
});

export const selectAssessmentDetails = createSelector(
  selectSelectedAssessment,
  (assessment) => assessment.content.details ?? {},
);

export const selectContactsList = createSelector(selectSelectedAssessment, (assessment) => {
  const entries = Object.values(assessment.content)?.filter((entry) => entry.type !== undefined);
  return [...new Set(entries.flatMap((entry) => entry.contacts))];
});

export const selectCatalogGroups = createSelector(selectSelectedAssessment, (assessment) =>
  assessment.content
    ? Object.values(assessment.content)
        ?.filter((entry) => entry.type === ItemTypes.GROUP && entry.id.split(':').length === 2)
        .sort((a, b) => naturalCompare(a.id, b.id))
    : [],
);

export const selectGroupControls = createSelector(
  [selectSelectedAssessment, (_, groupId) => groupId],
  (assessment, groupId) => {
    const idTail = groupId.split('.').length;
    return assessment.content
      ? Object.values(assessment.content)
          .filter((entry) =>
            entry.type !== ItemTypes.CONTROL || entry.id === groupId || !entry.id.startsWith(groupId)
              ? false
              : groupId.split(':').length === 2 // Title and first level of groups have the same length
                ? entry.id.split('.').length === idTail // Ensure only direct subgroups are listed
                : entry.id.split('.').length === idTail || entry.id.split('.').length === idTail + 1,
          )
          .sort((a, b) => naturalCompare(a.id, b.id))
      : [];
  },
);

export const selectSubControls = createSelector(
  [selectSelectedAssessment, (_, groupId) => groupId],
  (assessment, groupId) =>
    Object.values(assessment.content)
      .filter(
        (item) => (item.type === ItemTypes.CONTROL || item.type === ItemTypes.GROUP) && isDirectSub(item.id, groupId),
      )
      .sort((a, b) => naturalCompare(a.id, b.id)),
);

export const selectAllGroups = createSelector(
  selectSelectedAssessment,
  (assessment) =>
    assessment.content
      ? Object.values(assessment.content)
          .filter((entry) => entry.type === ItemTypes.GROUP)
          .sort((a, b) => naturalCompare(a.id, b.id))
      : [],
  {
    memoizeOptions: {
      equalityCheck: (a, b) => a.id === b.id,
    },
  },
);

export const selectSelectedGroupId = (state) => state.assessments.selectedGroupId;

export const selectAllContent = createSelector(selectSelectedAssessment, (assessment) =>
  assessment.content ? Object.values(assessment.content).filter((entry) => entry.type !== undefined) : [],
);

export const selectContacts = createSelector(
  [selectAllContent, (_, id) => id],
  (content, id) => content.find((entry) => entry.id === id)?.contacts ?? [],
);

export const selectAllControls = createSelector(selectSelectedAssessment, (assessment) => {
  const content = assessment.content ?? [];
  const controls = Object.values(content).filter((entry) => entry.inScope && entry.type === ItemTypes.CONTROL);
  return controls.sort(sorter);
});

export const selectIncompleteControls = createSelector(
  selectAllControls,
  (controls) => controls?.filter((entry) => entry.evaluationStatus !== EvaluationStatus.COMPLETED) || [],
);

export const selectMissingFindings = createSelector(selectAllControls, selectFindingsList, (controls, findings) =>
  controls.filter(
    (control) =>
      control.conclusion === Conclusions.ISSUES && !findings.some((finding) => finding.controlId === control.id),
  ),
);

const selectEvaluatedCount = createSelector(
  selectAllControls,
  (controls) => controls?.filter((entry) => entry.evaluationStatus === EvaluationStatus.COMPLETED)?.length || 0,
);

export const selectControlCount = createSelector(selectSelectedAssessment, (assessment) => (
  assessment.content
    ? Object.values(assessment.content).filter((entry) => entry.inScope && entry.type === ItemTypes.CONTROL).length
    : 0
));

export const selectEvaluationComplete = createSelector(
  selectControlCount,
  selectEvaluatedCount,
  (controlCount, evaluatedCount) => evaluatedCount !== 0 && evaluatedCount === controlCount,
);

export const selectAssessmentDone = (state) => state.assessments.selectedAssessment.done;

export const selectAssessmentTitle = createSelector(
  [selectAssessments, (_, assessmentId) => assessmentId],
  (assessments, assessmentId) => assessments.find((ass) => ass.id === assessmentId)?.name || 'Not found',
);

export const selectAssessmentFrameworkId = createSelector(
  [selectAssessments, (_, assessmentId) => assessmentId],
  (assessments, assessmentId) => assessments.find((ass) => ass.id === assessmentId)?.frameworkId || '',
);

export const selectCompletion = createSelector(
  selectControlCount,
  selectEvaluatedCount,
  (controlCount, evaluatedCount) => {
    if (controlCount === 0) {
      return '0';
    }
    const ratio = Math.round((evaluatedCount / controlCount) * 1000) / 10;
    return ratio.toString();
  },
);

export const selectStatusFilters = (state) => state.assessments.statusFilters;
export const selectConclusionFilters = (state) => state.assessments.conclusionFilters;
export const selectContactFilters = (state) => state.assessments.contactFilters;
export const selectFilterStatus = (state) => state.assessments.filterStatus;
export const selectFilterConclusion = (state) => state.assessments.filterConclusion;
export const selectFilterContacts = (state) => state.assessments.filterContacts;

export const selectFilteredList = createSelector(
  selectAllContent,
  selectAllGroups,
  selectFilterConclusion,
  selectConclusionFilters,
  selectFilterStatus,
  selectStatusFilters,
  selectFilterContacts,
  selectContactFilters,
  (
    content,
    groups,
    filterConclusion,
    conclusionFilters,
    filterStatus,
    statusFilters,
    filterContacts,
    contactFilters,
  ) => {
    const controls = content.filter(
      (entry) =>
        entry.type === ItemTypes.CONTROL &&
        entry.inScope &&
        (!filterConclusion ||
          conclusionFilters.includes(entry.conclusion) ||
          (conclusionFilters.includes('undefined') && entry.conclusion === '')) &&
        (!filterStatus || statusFilters.includes(entry.evaluationStatus)) &&
        (!filterContacts ||
          entry.contacts.some((person) => contactFilters.includes(person)) ||
          (contactFilters.includes('unassigned') && entry.contacts.length === 0)),
    );
    const filteredGroups = groups.filter((group) => controls.some((control) => control.id.startsWith(group.id)));
    return [...controls, ...filteredGroups].sort((a, b) => naturalCompare(a.id, b.id));
  },
);

const isCatalog = (item) => item.split(':').length === 2;

export const selectFilteredCatalogs = createSelector(selectFilteredList, (controls) =>
  controls.filter((control) => control.type === ItemTypes.GROUP && isCatalog(control.id)),
);

export const selectFilteredSubControls = createSelector(
  [selectFilteredList, (_, groupId) => groupId],
  (controls, groupId) => controls.filter((item) => isDirectSub(item.id, groupId)),
);

export const selectSelectedSubControls = createSelector(
  selectFilteredList,
  selectSelectedGroupId,
  (controls, groupId) =>
    controls.filter((control) => control.type === ItemTypes.CONTROL && control.id.startsWith(groupId)),
);

export const selectPdfDownloadStatus = (state) => state.assessments.pdfDownloadStatus;
